// The weather ground shader (docs/20 W4): one board-sized quad drawn in the light layer *under*
// the light map, so night darkens the fog and a torch lights it, and the gameplay marks and units
// above it are never weathered. It composes, in order: cloud shadows drifting with the wind, wet
// earth, puddles that reflect the sky with rain rings on them, snow cover with a pixel glint, and
// two layers of domain-warped fog; and for docs/23 rime (crystals on open land and wall tops, ice at
// the water's edge, a few glints), a heat shimmer (thin bright bands wobbling upward, a bleach), and
// sand drifts (the snow cover in a sand colour, drifting along the wind). Output is premultiplied (Phaser's NORMAL blend), so every step
// is "darken by d" or "lerp toward colour X by t" — the TS side caps each of d and t (weather.ts).
//
// The mask texture has one texel per cell: R = open land (1) or wall / tree / water (0), G = a per-cell
// roll for puddles (0.25..1) or water (0), B = an exit (indoors the rain only reaches those). Sampling it linearly gives soft edges between ground and walls for free.
export const GROUND_FRAG = `
#pragma phaserTemplate(shaderName)
#pragma phaserTemplate(extensions)
#pragma phaserTemplate(features)
#ifdef GL_FRAGMENT_PRECISION_HIGH
precision highp float;
#else
precision mediump float;
#endif
#pragma phaserTemplate(fragmentDefine)
varying vec2 outTexCoord;
#pragma phaserTemplate(outVariables)
#pragma phaserTemplate(fragmentHeader)
uniform sampler2D uMask;
uniform vec2 uSize;       // board size in art pixels
uniform vec2 uCells;      // board size in cells
uniform float uTime;      // seconds (frozen under reduce motion)
uniform float uWind;      // -1..1
uniform float uCloud;     // cloud-shadow depth
uniform float uCover;     // sky cover 0..1
uniform float uWet;       // 0..1
uniform float uDarkMax;   // the most the ground may be darkened
uniform float uFog;       // fog wash cap
uniform vec3 uFogColour;
uniform float uSnow;      // snow wash cap
uniform vec3 uSky;
uniform float uRipples;   // 0 when there is no rain falling on the puddles
uniform float uIndoor;    // 1 indoors: only the doorways (mask B) get wet
uniform float uPuddleDark; // how much darker a puddle is than its ground (PUDDLE_DARK)
uniform float uFogSpeed;  // 1 a drift .. 4.5 a sand storm driving across
uniform float uFogStretch; // >1 streaks the fog (and drifts) along the wind
uniform vec3 uSnowColour; // white snow, or sand
uniform float uSnowCaps;  // 0..1 how much the cover caps walls and canopies
uniform float uSnowDrifts; // 0 an even speckled dusting (sleet) .. 1 drifts
uniform float uFrost;     // rime cap (FROST_CAP)
uniform float uHeat;      // shimmer cap (HEAT_CAP)

float hash(vec2 p) { p = fract(p * vec2(123.34, 456.21)); p += dot(p, p + 45.32); return fract(p.x * p.y); }
float noise(vec2 p) {
  vec2 i = floor(p), f = fract(p);
  vec2 u = f * f * (3.0 - 2.0 * f);
  return mix(mix(hash(i), hash(i + vec2(1.0, 0.0)), u.x), mix(hash(i + vec2(0.0, 1.0)), hash(i + vec2(1.0, 1.0)), u.x), u.y);
}
float fbm(vec2 p) {
  float v = 0.0, a = 0.5;
  for (int i = 0; i < 4; i++) { v += a * noise(p); p = p * 2.03 + vec2(17.1, 9.2); a *= 0.5; }
  return v;
}

// premultiplied accumulator: result = C + dst * (1 - A)
vec4 darken(vec4 acc, float d) { return vec4(acc.rgb * (1.0 - d), 1.0 - (1.0 - acc.a) * (1.0 - d)); }
vec4 toward(vec4 acc, vec3 x, float t) { return vec4(acc.rgb * (1.0 - t) + x * t, 1.0 - (1.0 - acc.a) * (1.0 - t)); }

void main() {
  vec2 uv = vec2(outTexCoord.x, 1.0 - outTexCoord.y);   // top-left origin, like the board
  vec2 px = floor(uv * uSize) + 0.5;                       // the art pixel this fragment is in
  vec2 cell = px / uSize * uCells;                         // board position in cells
  vec4 m = texture2D(uMask, outTexCoord);                  // canvas textures upload bottom-up: no flip
  float open = m.r;
  float exit = m.b;
  float water = 1.0 - smoothstep(0.08, 0.22, m.g);
  float t = uTime;
  vec4 acc = vec4(0.0);

  // cloud shadows: big soft shapes sailing with the wind; more sky covered, more shadow
  vec2 cq = cell * 0.13 - vec2(t * (0.02 + 0.05 * uWind), t * 0.008);
  float cn = fbm(cq + fbm(cq * 0.7 + 3.1) * 0.6);
  float shade = smoothstep(0.62 - 0.22 * uCover, 0.78 - 0.12 * uCover, cn) * uCloud;

  // wet earth darkens; puddles gather where the ground is open and the roll and the noise agree.
  // Indoors only the doorways get wet (mask B), where the rain blows in.
  float wetHere = uWet * mix(1.0, exit, uIndoor);
  float pn = fbm(cell * 1.1 + 7.3) + (m.g - 0.62) * 0.4;
  float puddle = wetHere * open * step(0.66, pn);
  float wetDark = wetHere * (0.35 + 0.65 * open) * 0.5 * uDarkMax;
  float d = min(uDarkMax, 1.0 - (1.0 - shade) * (1.0 - wetDark));
  acc = darken(acc, d);

  if (puddle > 0.0) {
    // still water is a material, like the river tiles: darker than the ground it lies on, a cold
    // cast, and the sky caught in a one-pixel rim along the far (upper) bank
    acc = darken(acc, puddle * uPuddleDark);
    acc = toward(acc, vec3(0.16, 0.22, 0.34), puddle * 0.18);
    float above = fbm((px - vec2(0.0, 1.0)) / uSize * uCells * 1.1 + 7.3) + (m.g - 0.62) * 0.4;
    float rim = puddle * (1.0 - step(0.66, above));
    acc = toward(acc, mix(uSky, vec3(1.0), 0.2), rim * 0.4);
    float glint = smoothstep(0.74, 0.9, noise(cell * 3.0 + vec2(t * 0.15, 0.0)));
    acc = toward(acc, uSky, puddle * glint * 0.1);
    // rain rings: one per half cell, each on its own clock
    vec2 sub = cell * 2.0;
    vec2 id = floor(sub);
    float h = hash(id);
    float period = 0.8 + h * 0.9;
    float age = fract(t / period + h * 7.0);
    vec2 centre = id + 0.25 + 0.5 * vec2(hash(id + 3.7), hash(id + 9.1));
    float r = age * 0.42;
    float ring = step(abs(length(sub - centre) - r), 0.045) * step(0.35, hash(id + floor(t / period + h * 7.0)));
    acc = toward(acc, mix(uSky, vec3(1.0), 0.5), uRipples * puddle * ring * (1.0 - age) * 0.75);
  }

  // snow (or sand): a thin even dusting everywhere, crisp-edged drifts on open ground (stretched
  // along a gale), caps on walls and canopies — the edges are dithered per art pixel so it reads as
  // pixel art, not a wash. Sleet only dusts: a speckle, no drifts.
  if (uSnow > 0.0) {
    float drift = fbm(cell * vec2(0.7 / uFogStretch, 0.7 * min(uFogStretch, 1.4)) + 11.0) + (hash(px) - 0.5) * 0.06;
    float lay = mix(0.35 * uSnowCaps, 1.0, open) * (1.0 - water);   // walls and canopies take a cap; water none
    float patch = step(0.47, drift);
    float deep = step(0.58, drift) * open;
    float speck = step(0.5, hash(px + 5.1) + (drift - 0.5) * 0.8);
    float cover = uSnow * lay * (mix(0.62, 0.32, uSnowDrifts) + uSnowDrifts * (0.33 * patch + 0.35 * deep) + (1.0 - uSnowDrifts) * 0.38 * speck);
    acc = toward(acc, uSnowColour, cover);
    // a few pixels catch the light and twinkle
    float glint = step(0.994, hash(px)) * patch * uSnowDrifts * (0.5 + 0.5 * sin(t * 2.5 + hash(px + 1.3) * 40.0));
    acc = toward(acc, mix(uSnowColour, vec3(1.0), 0.7), glint * uSnow);
  }

  // rime: a pale cold breath over open land, sparse pixel crystals (little crosses on a 6 px grid)
  // on open land and wall / canopy tops, ice on the water's edge, a few glints
  if (uFrost > 0.0) {
    vec2 tc = (floor(outTexCoord * uCells) + 0.5) / uCells;
    float waterCell = 1.0 - step(0.08, texture2D(uMask, tc).g);
    float lay = mix(0.45, 1.0, open) * (1.0 - waterCell);
    float rn = noise(cell * 0.9 + 21.0);
    acc = toward(acc, vec3(0.86, 0.92, 1.0), uFrost * 0.32 * lay * (0.55 + 0.45 * rn));
    vec2 fp = floor(px);
    vec2 id = floor(fp / 6.0);
    vec2 c = id * 6.0 + 1.0 + floor(vec2(hash(id + 0.17), hash(id + 3.3)) * 4.0);
    vec2 dd = abs(fp - c);
    float arm = (dd.x < 0.5 && dd.y < 1.5) || (dd.y < 0.5 && dd.x < 1.5) ? 1.0 : 0.0;
    float core = dd.x < 0.5 && dd.y < 0.5 ? 1.0 : 0.0;
    float present = step(hash(id + 7.7), (0.12 + 0.5 * rn) * lay);
    acc = toward(acc, vec3(0.97, 0.99, 1.0), uFrost * present * mix(0.55, 1.0, core) * arm);
    acc = toward(acc, vec3(0.92, 0.96, 1.0), uFrost * 0.7 * step(0.95, hash(fp + 2.9)) * lay);
    // ice: a band along the bank of every water cell, starting where the shore art ends (~3 px in)
    // and 1–5 art pixels wide. Exact, not filtered: the eight neighbouring cells are sampled at their
    // centres; cardinals give straight banks, diagonals round the corners (a one-wide channel keeps
    // open water down its middle)
    vec2 f = fract(outTexCoord * uCells);
    vec2 cs = 1.0 / uCells;
    float land = 1.0e3;
    if (texture2D(uMask, tc + vec2(cs.x, 0.0)).g >= 0.08) land = min(land, 1.0 - f.x);
    if (texture2D(uMask, tc - vec2(cs.x, 0.0)).g >= 0.08) land = min(land, f.x);
    if (texture2D(uMask, tc + vec2(0.0, cs.y)).g >= 0.08) land = min(land, 1.0 - f.y);
    if (texture2D(uMask, tc - vec2(0.0, cs.y)).g >= 0.08) land = min(land, f.y);
    if (texture2D(uMask, tc + cs).g >= 0.08) land = min(land, length(vec2(1.0) - f));
    if (texture2D(uMask, tc - cs).g >= 0.08) land = min(land, length(f));
    if (texture2D(uMask, tc + vec2(cs.x, -cs.y)).g >= 0.08) land = min(land, length(vec2(1.0 - f.x, f.y)));
    if (texture2D(uMask, tc + vec2(-cs.x, cs.y)).g >= 0.08) land = min(land, length(vec2(f.x, 1.0 - f.y)));
    float edgePx = land * uSize.x / uCells.x - 2.5;
    float width = 1.0 + 4.0 * smoothstep(0.25, 0.75, noise(cell * 1.7 + 5.0)) + (hash(fp) - 0.5) * 1.2;
    float bank = waterCell * step(0.0, edgePx) * (1.0 - step(width, edgePx));
    float lip = waterCell * step(width - 1.0, edgePx) * (1.0 - step(width, edgePx));   // the ice's outer edge catches the light
    acc = toward(acc, vec3(0.84, 0.92, 0.99), min(1.0, uFrost * 1.4) * bank);
    acc = toward(acc, vec3(0.97, 0.99, 1.0), uFrost * 0.9 * lip);
    acc = toward(acc, vec3(0.8, 0.88, 0.98), uFrost * 0.18 * waterCell);
    float tw = step(0.996, hash(fp + 9.4)) * (0.5 + 0.5 * sin(t * 3.1 + hash(fp) * 50.0));
    acc = toward(acc, vec3(1.0), tw * uFrost * 1.4 * max(lay, bank));
  }

  // heat: the whole open ground bleached a little, and thin bright bands wobbling upward in patches —
  // a shimmer that only ever lightens, so nothing under it is hidden
  if (uHeat > 0.0) {
    acc = toward(acc, vec3(1.0, 0.95, 0.82), uHeat * 0.5 * (0.4 + 0.6 * open));
    float wob = sin(px.y * 0.55 + t * 5.0 + sin(px.x * 0.11 + t * 1.3) * 2.2);
    float where = smoothstep(0.45, 0.72, noise(cell * 0.45 + vec2(t * 0.08, t * 0.35)));
    float band = step(0.86, wob) * where * (0.5 + 0.5 * open);
    acc = toward(acc, vec3(1.0, 0.98, 0.9), uHeat * band);
  }

  // fog: two layers of warped noise moving at different speeds, thicker in hollows (open ground)
  if (uFog > 0.0) {
    float ft = t * uFogSpeed;
    vec2 sc = vec2(1.0 / uFogStretch, min(uFogStretch, 1.5));
    vec2 q = cell * 0.32 * sc - vec2(ft * (0.03 + 0.09 * uWind), ft * 0.012);   // both layers drift with the wind
    float w = fbm(q * 1.6 + vec2(ft * 0.04, -ft * 0.02));
    float f1 = fbm(q + w * 1.3);
    float f2 = fbm(cell * 0.55 * sc - vec2(ft * (0.05 + 0.13 * uWind), ft * 0.02) + 4.0);
    float dens = clamp(0.25 + smoothstep(0.32, 0.78, f1) * 0.6 + smoothstep(0.45, 0.8, f2) * 0.35, 0.0, 1.0);
    acc = toward(acc, uFogColour, uFog * dens * mix(0.75, 1.0, open));
  }

  gl_FragColor = acc;
}
`;
